import { useEffect, useRef } from "react";
import { useQuery } from "@tanstack/react-query";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { Bike, Phone, Clock, MapPin, Navigation, Loader2 } from "lucide-react";
import { getRiderLocation } from "@/lib/api";

// Rider-on-bike marker (green badge with a bike icon).
const bikeIcon = L.divIcon({
  className: "",
  html: `<div style="background:#16a34a;width:38px;height:38px;border-radius:50%;display:flex;align-items:center;justify-content:center;box-shadow:0 2px 8px rgba(0,0,0,.35);border:3px solid #fff">
    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><circle cx="18.5" cy="17.5" r="3.5"/><circle cx="5.5" cy="17.5" r="3.5"/><circle cx="15" cy="5" r="1"/><path d="M12 17.5V14l-3-3 4-3 2 3h2"/></svg>
  </div>`,
  iconSize: [38, 38],
  iconAnchor: [19, 19],
});

const dropIcon = L.divIcon({
  className: "",
  html: `<div style="background:#dc2626;width:30px;height:30px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);box-shadow:0 2px 6px rgba(0,0,0,.3);border:2px solid #fff"></div>`,
  iconSize: [30, 30],
  iconAnchor: [15, 28],
});

function since(iso) {
  if (!iso) return null;
  const s = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  if (s < 60) return `${s}s ago`;
  return `${Math.round(s / 60)}m ago`;
}

export default function RiderTracker({ order }) {
  const mapRef = useRef(null);
  const mapObj = useRef(null);
  const riderMarker = useRef(null);
  const dropMarker = useRef(null);

  const { data } = useQuery({
    queryKey: ["rider-location", order.id],
    queryFn: async () => (await getRiderLocation(order.id)).data,
    refetchInterval: 10000,
    refetchIntervalInBackground: true,
  });

  const loc = data || {};
  const hasFix = loc.online && loc.lat != null && loc.lng != null;
  const drop = order.delivery_lat != null && order.delivery_lng != null
    ? [order.delivery_lat, order.delivery_lng] : null;
  const riderName = order.partner_name || order.driver_name;

  // Init the map once we have any coordinate to show.
  useEffect(() => {
    if (!mapRef.current || mapObj.current) return;
    if (!hasFix && !drop) return;
    const center = hasFix ? [loc.lat, loc.lng] : drop;
    mapObj.current = L.map(mapRef.current, { zoomControl: false, attributionControl: false }).setView(center, 14);
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(mapObj.current);
    if (drop) dropMarker.current = L.marker(drop, { icon: dropIcon }).addTo(mapObj.current);
    return () => { mapObj.current?.remove(); mapObj.current = null; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasFix, !!drop]);

  // Move the rider marker + fit bounds on each update.
  useEffect(() => {
    const map = mapObj.current;
    if (!map) return;
    if (hasFix) {
      const pos = [loc.lat, loc.lng];
      if (!riderMarker.current) riderMarker.current = L.marker(pos, { icon: bikeIcon }).addTo(map);
      else riderMarker.current.setLatLng(pos);
      const pts = [pos];
      if (drop) pts.push(drop);
      map.fitBounds(L.latLngBounds(pts).pad(0.4), { maxZoom: 16 });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loc.lat, loc.lng, hasFix]);

  if (!riderName && !hasFix) return null; // no rider assigned yet

  return (
    <div data-testid="rider-tracker">
      <p className="text-xs uppercase tracking-[0.12em] text-muted-foreground font-semibold mb-3">Delivery partner</p>

      <div className="rounded-md border border-border overflow-hidden">
        {/* Rider identity + ETA */}
        <div className="flex items-center justify-between gap-3 p-3.5">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-full bg-emerald-600 grid place-items-center shrink-0">
              <Bike className="h-5 w-5 text-white" />
            </div>
            <div className="min-w-0">
              <p className="font-semibold truncate" data-testid="rider-name">{riderName || "Rider"}</p>
              <p className="text-xs text-muted-foreground truncate">
                {[order.vehicle_type, order.vehicle_number].filter(Boolean).join(" · ") || "Delivery partner"}
              </p>
            </div>
          </div>
          {order.driver_phone && (
            <a href={`tel:${order.driver_phone}`} data-testid="rider-call"
               className="inline-flex items-center gap-1.5 text-sm font-medium rounded-md border border-border px-3 py-2 hover:bg-accent transition-colors shrink-0">
              <Phone className="h-4 w-4" /> Call
            </a>
          )}
        </div>

        {/* Live status row */}
        <div className="flex items-center gap-4 px-3.5 pb-3 text-sm flex-wrap">
          <span className={`inline-flex items-center gap-1.5 font-medium ${hasFix ? "text-emerald-600" : "text-muted-foreground"}`} data-testid="rider-online">
            <span className={`h-2 w-2 rounded-full ${hasFix ? "bg-emerald-500 animate-pulse" : "bg-muted-foreground/50"}`} />
            {hasFix ? "Live" : "Waiting for location…"}
          </span>
          {loc.eta_min != null && (
            <span className="inline-flex items-center gap-1.5" data-testid="rider-eta">
              <Clock className="h-4 w-4 text-muted-foreground" /> ETA <b>{Math.round(loc.eta_min)} min</b>
            </span>
          )}
          {loc.distance_km != null && (
            <span className="inline-flex items-center gap-1.5" data-testid="rider-distance">
              <Navigation className="h-4 w-4 text-muted-foreground" /> {Number(loc.distance_km).toFixed(2)} km away
            </span>
          )}
          {loc.last_ping && (
            <span className="text-xs text-muted-foreground">updated {since(loc.last_ping)}</span>
          )}
        </div>

        {/* Map */}
        {(hasFix || drop) ? (
          <div ref={mapRef} data-testid="rider-map" style={{ height: 240, width: "100%" }} className="bg-muted" />
        ) : (
          <div className="h-[120px] grid place-items-center text-sm text-muted-foreground border-t border-border">
            <span className="inline-flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Rider location will appear once they’re on the way</span>
          </div>
        )}

        {order.delivery_address && (
          <div className="flex items-start gap-2 p-3 border-t border-border text-sm">
            <MapPin className="h-4 w-4 text-muted-foreground shrink-0 mt-0.5" />
            <span className="text-muted-foreground">{order.delivery_address}</span>
          </div>
        )}
      </div>
    </div>
  );
}
